<template>
  <div class="attachment-page">
    <div v-if="storeNameLocal" class="store-header py-2 px-4 mt-2">
      {{ storeNameLocal }}
    </div>

    <div v-if="loading" class="flex justify-center py-5">
      <van-loading type="spinner" />
    </div>

    <div v-else-if="!hasAttachments" class="flex justify-center py-5">
      <van-empty description="No attachments" />
    </div>

    <div v-else class="attachment-content">
      <div class="file-list bg-white">
        <div v-for="group in groupedAttachments" :key="group.docType" class="group-section mb-8">
          <!-- Title Row with Name and Type -->
          <div
            class="file-title-row flex justify-between items-end pb-2 mb-2 border-b border-[#ebedf0] mt-4 px-4"
          >
            <div class="file-title flex-1 min-w-0 flex flex-wrap items-center mr-2">
              <span v-if="group.isRequired" class="text-[#ee0a24] mr-1 shrink-0">*</span>
              <span class="font-bold text-[#323233] leading-tight mr-2" :title="group.title">
                {{ group.title }}
              </span>
              <span class="text-[#969799] text-xs whitespace-nowrap shrink-0">
                ({{ group.formatHint ? group.formatHint + ' ' : '' }}≤{{ group.sizeLimitMB }}MB)
              </span>
            </div>
            <div class="action-buttons flex gap-2 ml-2 shrink-0">
              <div
                v-if="canUploadOrDelete.canUpload(group.docType)"
                class="dark-btn cursor-pointer"
                @click.stop="handleUploadItem(group)"
              >
                UPLOAD
              </div>
            </div>
          </div>

          <div
            v-for="(item, index) in group.items"
            :key="item.id || index"
            :class="['file-item px-4', { 'pb-4': index !== group.items.length - 1 }]"
          >
            <!-- File Content Row (Icon + Name + Meta + Download) -->
            <div class="file-content-row flex justify-between items-start">
              <div class="left-content flex items-start flex-1 min-w-0">
                <div class="action-icon mr-2" v-if="canUploadOrDelete.canDelete(item)">
                  <van-icon
                    name="delete-o"
                    color="#ee0a24"
                    size="20"
                    @click.stop="handleDelete(item)"
                  />
                </div>
                <div class="file-info flex-1 min-w-0">
                  <div
                    class="file-ori-name flex items-center flex-wrap"
                    @click.stop="handlePreview(item)"
                  >
                    <span class="mr-1 break-all">{{ item.oriName }}</span>
                    <!-- <img src="@/assets/gc-bi/icon/svg/view.svg" class="w-4 h-4 cursor-pointer" /> -->
                  </div>
                  <div class="file-meta flex items-center justify-end mt-1 pr-2 text-xs">
                    <span class="upload-by pr-3 text-[#B9B9B9]"
                      >Upload By {{ item.createUser }}</span
                    >
                    <span class="upload-date text-[#7F7F7F]">{{
                      formatDate(item.createDate)
                    }}</span>
                  </div>
                </div>
              </div>

              <div
                class="right-content ml-2 shrink-0 flex justify-start"
                v-if="item.url && !isDoorSummaryView && !isDoorSummaryAllorOpened"
              >
                <div class="dark-btn cursor-pointer" @click.stop="handlePreview(item)">VIEW</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <van-overlay :show="showViewer && viewerMode === 'html'" z-index="2000">
      <div class="wrapper fixed inset-0 flex items-center justify-center">
        <div id="pptx-container" class="doc-viewer-html"></div>
      </div>
    </van-overlay>

    <ImageViewer v-model:show="imageViewerShow" :src="viewerUrl" :watermark-text="userName" />
    <PdfViewer v-model:show="pdfViewerShow" :src="viewerUrl" :watermark-text="userName" />

    <input type="file" ref="fileInput" style="display: none" @change="onFileChange" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { showToast, showDialog } from 'vant'
import {
  getAttachments,
  getPresignedUrl,
  uploadAttachment,
  deleteAttachment,
} from '@/api/attachment'
import {
  docTypeList,
  attachmentGateConfig,
  dcAttachmentGateConfig,
  requiredAttachmentConfig,
  openingPictureLabelMap,
  openingPictureTypeArr,
} from '@/utils/attachment-config'
import { approvalsTab } from '@/utils/config'
import { getFormat } from '@/utils/word'
import { formatDate } from '@/utils/utils'
import { getCurrentUser } from '@/api/login'
import { useUserStore } from '@/stores/user'
// @ts-ignore
import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs'
import ImageViewer from '@/components/ImageViewer.vue'
import PdfViewer from '@/components/PdfViewer.vue'

const props = defineProps<{ detailData: any; noGate?: boolean }>()

const route = useRoute()
const userStore = useUserStore()
const { roleGroupList } = storeToRefs(userStore)
const isDoorSummaryView = computed(() => String(route.query.fromDoorSummary || '') === '1')
const batchId = computed(() => String(route.query.batchId || ''))
const storeNameLocal = computed(() => String(route.query.storeNameLocal || ''))
const currentTaskName = computed(() => String(route.query.currentTaskName || ''))
const clcDoorCode = computed(() => String(route.query.clcDoorCode || ''))
const taskId = computed(() => String(route.query.taskId || ''))
const isDoorSummaryAllorOpened = computed(() => {
  const tab = String(route.query.tabIndex || '')
  const fromDoorSummary = String(route.query.fromDoorSummary || '') === '1'
  return fromDoorSummary && (tab === '0' || tab === '4')
})

const loading = ref(false)
const attachments = ref<any[]>([])
const showViewer = ref(false)
const viewerUrl = ref('')
const viewerMode = ref<'image' | 'pdf' | 'html'>('image')
const pdfLoading = ref(false)
const imageViewerShow = ref(false)
const pdfViewerShow = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const currentUploadGroup = ref<any>(null)
const userName = ref('')
const wmStyle = computed(() => {
  const text = userName.value || ''
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='260'><text x='30%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='rgba(0,0,0,0.12)' font-size='22' font-family='system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' transform='rotate(-30,130,130)'>${text}</text></svg>`
  const url = 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
  return { backgroundImage: `url("${url}")` }
})
const pdfContainer = ref<HTMLElement | null>(null)
let pdfLoadingTask: any = null
let pdfDoc: any = null
let pdfWorker: Worker | null = null
const pdfDragTouching = ref(false)
const pdfDragMouseDown = ref(false)
const pdfLastY = ref(0)
const pdfLastX = ref(0)
const pdfScale = ref(1)
const pdfTransformOrigin = ref('50% 0')
const pinchActive = ref(false)
const pinchStartDist = ref(0)
const pinchStartScale = ref(1)
let pdfBaseScale = 1
let pdfRerenderTimer: any = null
const ensurePdfWorker = () => {
  if (typeof window === 'undefined') return
  if (!pdfWorker) {
    pdfWorker = new Worker(new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url), {
      type: 'module',
    })
    ;(pdfjsLib as any).GlobalWorkerOptions.workerPort = pdfWorker
    pdfWorker.addEventListener('error', () => {
      try {
        pdfWorker?.terminate()
      } catch {}
      pdfWorker = null
      ;(pdfjsLib as any).GlobalWorkerOptions.workerPort = null
    })
  }
}

const processDefinitionKey = computed(() => props.detailData?.processDefinitionKey || '')
const storeDepth = computed(() => props.detailData?.storeDepth || '')
const storeWidth = computed(() => props.detailData?.storeWidth || '')
const storeHeight = computed(() => props.detailData?.storeHeight || '')
const isDCFlow = computed(() => {
  const dcKeys = approvalsTab.find((t) => t.key === 'D&C')?.processDefinitionKeys || []
  return dcKeys.includes(processDefinitionKey.value)
})

const isGateD1 = computed(() => currentTaskName.value.startsWith('D1'))
const isGateD3 = computed(() => currentTaskName.value.startsWith('D3'))
const isGateC3 = computed(() => currentTaskName.value.startsWith('C3'))
const isGateC4 = computed(() => currentTaskName.value.startsWith('C4'))
const storeFormat = computed(() => String(props.detailData?.format || '').toUpperCase())
const isGateN1dian1 = computed(() => currentTaskName.value.startsWith('N1.1'))
const isGateN1dian2 = computed(() => currentTaskName.value.startsWith('N1.2'))
const isGateN6 = computed(() => currentTaskName.value.startsWith('N6'))
const isRezoning = ['Top Door', 'FWW', 'Change Order']
const isRezoningCase = computed(() => {
  const pt = String(props.detailData?.projectType || '').trim()
  return isRezoning.includes(pt)
})
const canUpload = (docType: string) => {
  if (!isDCFlow.value) return true
  if (isGateD1.value) return docType === 'fp'
  if (isGateD3.value) return docType === 'fp' || docType === 'cd'
  if (isGateC3.value || isGateC4.value) {
    const isNonKey = storeFormat.value === 'NON-KEY'
    if (isNonKey) {
      return docType === 'facade'
    }
    return openingPictureTypeArr.includes(docType)
  }
  return false
}
const canDelete = (item: any) => {
  const byFlow = canUpload(item.docType)
  const isSelf = String(item.createUser || '').trim() === String(userName.value || '').trim()
  return byFlow && isSelf
}
const canUploadOrDelete = computed(() => {
  // 如果是Door Summary并且不是all，不能删除和上传
  if (!isDoorSummaryAllorOpened.value && isDoorSummaryView.value) {
    return { canUpload: () => false, canDelete: () => false }
  } else if (!isDoorSummaryAllorOpened.value) {
    return { canUpload: canUpload, canDelete: canDelete }
  }
  // Door Summary权限判断
  const hasStarsAdmin = roleGroupList.value.includes('STARS Admin')
  const hasDesignGroup = roleGroupList.value.includes('Design Group')
  const hasPGMGroup = roleGroupList.value.includes('PGM Group')
  if (hasStarsAdmin) {
    return {
      canUpload: () => true,
      canDelete: () => true
    }
  }

  const canUploadDoorSummary = (docType: string) => {
    if (hasDesignGroup && (docType === 'fp' || docType === 'cd')) return true
    if (hasPGMGroup && openingPictureTypeArr.includes(docType)) return true
    return false
  }

  if (hasDesignGroup || hasPGMGroup) {
    return {
      canUpload: canUploadDoorSummary,
      canDelete: () => false
    }
  }

  return {
    canUpload: () => false,
    canDelete: () => false
  }
})

const hasAttachments = computed(() => {
  return groupedAttachments.value.length > 0
})

const groupedAttachments = computed(() => {
  // 选择 All 时，特殊处理
  if (isDoorSummaryAllorOpened.value) {
    const doorStatusNorm = String(props.detailData?.status || '').trim().toLowerCase()
    const isOpened = doorStatusNorm === 'opened'

    const present = Array.from(new Set(attachments.value.map((a) => String(a.docType))))
    const orderIndex = (k: string) => {
      const i = docTypeList.findIndex((d) => d.name === k)
      return i === -1 ? Number.MAX_SAFE_INTEGER : i
    }
    const allowedTypes = present.sort((a, b) => orderIndex(a) - orderIndex(b))

    const groups: any[] = []
    allowedTypes.forEach((typeKey) => {
      const items = attachments.value.filter((item) => item.docType === typeKey)
      const typeConfig = docTypeList.find((t) => t.name === typeKey)
      const formatHint = getFormat(typeKey).label
      const titleLabel = (openingPictureLabelMap as any)[typeKey] || (typeConfig ? typeConfig.value : typeKey)
      
      groups.push({
        docType: typeKey,
        title: titleLabel,
        items: items,
        isRequired: false,
        formatHint,
        sizeLimitMB: typeKey === 'fp' || typeKey === 'cd' ? 100 : 20,
      })
      const isNonKey = storeFormat.value === 'NON-KEY'
      if (isNonKey) {
        const g = groups.find((x) => x.docType === 'facade')
        if (g) g.title = 'Opening Photo 1 - Facade（请至少上传1张）'
      }
    })
    
    if (!groups.some(g => g.docType === 'fp')) {
      const typeConfig = docTypeList.find((t) => t.name === 'fp')
      const formatHint = getFormat('fp').label
      const titleLabel = typeConfig ? typeConfig.value : 'fp'
      groups.push({
        docType: 'fp',
        title: titleLabel,
        items: [],
        isRequired: false,
        formatHint,
        sizeLimitMB: 100,
      })
    }
    
    if (!groups.some(g => g.docType === 'cd')) {
      const typeConfig = docTypeList.find((t) => t.name === 'cd')
      const formatHint = getFormat('cd').label
      const titleLabel = typeConfig ? typeConfig.value : 'cd'
      groups.push({
        docType: 'cd',
        title: titleLabel,
        items: [],
        isRequired: false,
        formatHint,
        sizeLimitMB: 100,
      })
    }

    // opened：在原有分组基础上，补齐 opening 分组（不替换其它类型）；NON-KEY 仅补齐 facade
    if (isOpened) {
      const isNonKey = storeFormat.value === 'NON-KEY'
      const openingTypesToEnsure = isNonKey ? (['facade'] as const) : openingPictureTypeArr
      for (const typeKey of openingTypesToEnsure) {
        if (groups.some((g) => g.docType === typeKey)) continue
        const items = attachments.value.filter((item) => item.docType === typeKey)
        const typeConfig = docTypeList.find((t) => t.name === typeKey)
        const formatHint = getFormat(typeKey).label
        const titleLabel =
          (openingPictureLabelMap as Record<string, string>)[typeKey] ||
          (typeConfig ? typeConfig.value : typeKey)
        groups.push({
          docType: typeKey,
          title: titleLabel,
          items,
          isRequired: false,
          formatHint,
          sizeLimitMB: 20,
        })
      }
      if (isNonKey) {
        const g = groups.find((x) => x.docType === 'facade')
        if (g) g.title = 'Opening Photo 1 - Facade（请至少上传1张）'
      }
    }

    // Door Summary（All / Opened）：KEY 门店固定展示 Others Photo 分组
    if (storeFormat.value === 'KEY' && !groups.some((g) => g.docType === 'others_photo')) {
      const typeKey = 'others_photo'
      const items = attachments.value.filter((item) => item.docType === typeKey)
      const typeConfig = docTypeList.find((t) => t.name === typeKey)
      const formatHint = getFormat(typeKey).label
      const titleLabel =
        (openingPictureLabelMap as Record<string, string>)[typeKey] ||
        (typeConfig ? typeConfig.value : typeKey)
      groups.push({
        docType: typeKey,
        title: titleLabel,
        items,
        isRequired: false,
        formatHint,
        sizeLimitMB: 20,
      })
    }

    // 过滤掉title为空的组
    return groups.filter((g) => String(g.title ?? '').trim() !== '')
  }

  const isDC = isDCFlow.value
  let allowedTypes: string[] = []

  if (props.noGate) {
    const present = Array.from(new Set(attachments.value.map((a) => String(a.docType))))
    const orderIndex = (k: string) => {
      const i = docTypeList.findIndex((d) => d.name === k)
      return i === -1 ? Number.MAX_SAFE_INTEGER : i
    }
    allowedTypes = present.sort((a, b) => orderIndex(a) - orderIndex(b))
  } else {
    if (isDC) {
      const name = currentTaskName.value
      if (name.startsWith('C3') || name.startsWith('C4')) {
        const isNonKey = storeFormat.value === 'NON-KEY'
        allowedTypes = isNonKey
          ? [...dcAttachmentGateConfig, 'facade']
          : [
              ...dcAttachmentGateConfig,
              ...openingPictureTypeArr,
            ]
      } else {
        allowedTypes = dcAttachmentGateConfig
      }
    } else {
      const name = currentTaskName.value
      let gateKey = ''
      for (const key of Object.keys(attachmentGateConfig)) {
        if (name.startsWith(key)) {
          gateKey = key
          break
        }
      }
      allowedTypes = gateKey ? attachmentGateConfig[gateKey] || [] : []
    }

    const projectTypeMain = String(props.detailData?.projectTypeMain || '').toUpperCase()
    allowedTypes = allowedTypes.filter((t) => {
      if (projectTypeMain === 'NEW') {
        if (t === 'mix_for_new_door') return true
        if (t === 'mix_for_reno_door_before' || t === 'mix_for_reno_door_after') return false
        return true
      }
      if (projectTypeMain === 'RENO') {
        if (t === 'mix_for_new_door') return false
        if (t === 'mix_for_reno_door_before' || t === 'mix_for_reno_door_after') return true
        return true
      }
      return true
    })
  }

  if (allowedTypes.length === 0) {
    return []
  }

  const groups: any[] = []

  allowedTypes.forEach((typeKey) => {
    const items = attachments.value.filter((item) => item.docType === typeKey)
    if (props.noGate && items.length === 0) return

    const typeConfig = docTypeList.find((t) => t.name === typeKey)

    let isRequired = false
    for (const key in requiredAttachmentConfig) {
      if (currentTaskName.value.startsWith(key)) {
        if (requiredAttachmentConfig[key] && requiredAttachmentConfig[key].includes(typeKey)) {
          isRequired = true
        }
        break
      }
    }

    if (typeKey === 'mix_1st_reno_floor') {
      const floorVal = String(props.detailData?.floor || '')
        .trim()
        .toUpperCase()
      isRequired = floorVal !== '1F'
    }

    const projectTypeMain = String(props.detailData?.projectTypeMain || '').toUpperCase()
    if (!props.noGate) {
      if (projectTypeMain === 'NEW' && typeKey === 'mix_for_new_door') {
        isRequired = true
      }
      // RenoN1.1和N1.2必传before和after，Rezoning不必传
      if (
        projectTypeMain === 'RENO' && (isGateN1dian1.value || isGateN1dian2.value) && !isRezoningCase.value &&
        (typeKey === 'mix_for_reno_door_before' || typeKey === 'mix_for_reno_door_after')
      ) {
        isRequired = true
      }
    }


    const formatHint = getFormat(typeKey).label
    let titleLabel = (openingPictureLabelMap as any)[typeKey] || (typeConfig ? typeConfig.value : typeKey)
    if ((isGateC3.value || isGateC4.value) && storeFormat.value === 'NON-KEY' && typeKey === 'facade') {
      titleLabel = 'Opening Photo 1 - Facade（请至少上传1张）'
    }

    groups.push({
      docType: typeKey,
      title: titleLabel,
      items: items,
      isRequired,
      formatHint,
      sizeLimitMB: isDC ? 100 : 20,
    })
  })

  // 过滤掉title为空的组
  return groups.filter((g) => String(g.title ?? '').trim() !== '')
})

const fetchData = async () => {
  if (!batchId.value) return

  loading.value = true
  try {
    const res = await getAttachments(batchId.value)
    if (res.data && Array.isArray(res.data)) {
      attachments.value = res.data.filter(
        (a: any) => String(a.docType ?? '').trim() !== '',
      )
    } else {
      attachments.value = []
    }
  } catch (error) {
    console.error('Failed to fetch attachments:', error)
    showToast('Failed to load attachments')
  } finally {
    loading.value = false
  }
}

const handleDelete = (item: any) => {
  showDialog({
    title: 'Operation Confirm',
    message: 'Please double confirm your operation?',
    showCancelButton: true,
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
  })
    .then(async () => {
      try {
        await deleteAttachment([item.id])
        showToast('Delete successful')
        fetchData()
      } catch (error) {
        console.error('Delete failed:', error)
      }
    })
    .catch(() => {
      // on cancel
    })
}

const router = useRouter()

const handlePreview = async (item: any) => {
  try {
    // 使用 item.url 获取预签名 URL
    if (item.url) {
      const res = await getPresignedUrl(item.url, false)
      const presignedUrl = res.data

      if (presignedUrl) {
        const fileType = (item.fileType || '').toLowerCase()
        const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'].some((ext) =>
          fileType.includes(ext),
        )
        const isPpt = ['.ppt', '.pptx'].some((ext) => fileType.includes(ext))
        const isPdf = fileType.includes('.pdf')
        const isOffice = ['.doc', '.docx', '.xls', '.xlsx'].some((ext) => fileType.includes(ext))

        if (isImage) {
          viewerMode.value = 'image'
          viewerUrl.value = presignedUrl
          imageViewerShow.value = true
        } else if (isPpt) {
          router.push({
            path: '/officePreview',
            query: {
              src: presignedUrl,
              wm: userName.value,
              return: `/approvalsDetail?${new URLSearchParams({ ...(route.query as any), tab: 'attachment' }).toString()}`,
            },
          })
        } else if (isPdf) {
          viewerUrl.value = toProxyUrl(presignedUrl)
          pdfViewerShow.value = true
        } else if (isOffice) {
          router.push({
            path: '/officePreview',
            query: {
              src: presignedUrl,
              wm: userName.value,
              return: `/approvalsDetail?${new URLSearchParams({ ...(route.query as any), tab: 'attachment' }).toString()}`,
            },
          })
        } else {
          window.open(presignedUrl, '_blank')
        }
      } else {
        showToast('Unable to get file link')
      }
    } else {
      showToast('File identifier (url) does not exist')
    }
  } catch (error) {
    console.error('Preview failed:', error)
  }
}

const toProxyUrl = (u: string) => {
  try {
    const parsed = new URL(u)
    if (parsed.hostname.includes('amazonaws.com')) {
      return '/s3' + parsed.pathname + parsed.search
    }
  } catch {}
  return u
}

const openPdf = async (url: string) => {
  if (!pdfContainer.value) return
  await clearPdf()
  ensurePdfWorker()
  pdfLoading.value = true
  try {
    pdfLoadingTask = pdfjsLib.getDocument({ url })
    const pdf = await pdfLoadingTask.promise
    pdfDoc = pdf
    const count = pdf.numPages
    for (let i = 1; i <= count; i++) {
      const page = await pdf.getPage(i)
      const viewport = page.getViewport({ scale: 1 })
      const scale = Math.min(1.5, (pdfContainer.value.clientWidth - 20) / viewport.width)
      if (i === 1) {
        pdfBaseScale = scale
        pdfScale.value = 1
      }
      const v = page.getViewport({ scale })
      const canvas = document.createElement('canvas')
      canvas.width = Math.floor(v.width)
      canvas.height = Math.floor(v.height)
      const ctx = canvas.getContext('2d')!
      await page.render({ canvasContext: ctx, viewport: v }).promise
      pdfContainer.value.appendChild(canvas)
    }
    pdfLoading.value = false
  } catch (err: any) {
    const msg = String(err?.message || '')
    if (msg.includes('Worker was terminated')) {
      try {
        try {
          pdfWorker?.terminate()
        } catch {}
        pdfWorker = null
        ;(pdfjsLib as any).GlobalWorkerOptions.workerPort = null
        ensurePdfWorker()
        pdfLoadingTask = pdfjsLib.getDocument({ url })
        const pdf = await pdfLoadingTask.promise
        pdfDoc = pdf
        const count = pdf.numPages
        for (let i = 1; i <= count; i++) {
          const page = await pdf.getPage(i)
          const viewport = page.getViewport({ scale: 1 })
          const scale = Math.min(1.5, (pdfContainer.value.clientWidth - 20) / viewport.width)
          if (i === 1) {
            pdfBaseScale = scale
            pdfScale.value = 1
          }
          const v = page.getViewport({ scale })
          const canvas = document.createElement('canvas')
          canvas.width = Math.floor(v.width)
          canvas.height = Math.floor(v.height)
          const ctx = canvas.getContext('2d')!
          await page.render({ canvasContext: ctx, viewport: v }).promise
          pdfContainer.value.appendChild(canvas)
        }
        pdfLoading.value = false
        return
      } catch (e) {
        pdfLoading.value = false
        throw e
      }
    }
    pdfLoading.value = false
    throw err
  }
}

const clearPdf = async () => {
  if (pdfContainer.value) pdfContainer.value.innerHTML = ''
  try {
    if (pdfDoc && pdfDoc.destroy) {
      await pdfDoc.destroy()
    } else if (pdfLoadingTask && pdfLoadingTask.destroy) {
      await pdfLoadingTask.destroy()
    }
  } catch {}
  pdfLoadingTask = null
  pdfDoc = null
  pdfLoading.value = false
}

const onPdfTouchStart = (e: TouchEvent) => {
  const el = pdfContainer.value
  if (!el) return
  if (e.touches && e.touches.length === 2) {
    pinchActive.value = true // @ts-ignore
    const x1 = e.touches[0].clientX // @ts-ignore
    const y1 = e.touches[0].clientY // @ts-ignore
    const x2 = e.touches[1].clientX // @ts-ignore
    const y2 = e.touches[1].clientY
    pinchStartDist.value = Math.hypot(x2 - x1, y2 - y1)
    pinchStartScale.value = pdfScale.value
    const rect = el.getBoundingClientRect()
    const cx = (x1 + x2) / 2 - rect.left + el.scrollLeft
    const cy = (y1 + y2) / 2 - rect.top + el.scrollTop
    pdfTransformOrigin.value = `${Math.max(0, Math.floor(cx))}px ${Math.max(0, Math.floor(cy))}px`
    pdfDragTouching.value = false
    return
  }
  const t = e.touches[0] || e.changedTouches[0]
  if (!t) return
  pdfDragTouching.value = true
  pdfLastY.value = t.clientY
  pdfLastX.value = t.clientX
}
const onPdfTouchMove = (e: TouchEvent) => {
  const el = pdfContainer.value
  if (!el) return
  if (pinchActive.value && e.touches && e.touches.length === 2) {
    // @ts-ignore
    const x1 = e.touches[0].clientX // @ts-ignore
    const y1 = e.touches[0].clientY // @ts-ignore
    const x2 = e.touches[1].clientX // @ts-ignore
    const y2 = e.touches[1].clientY
    const dist = Math.hypot(x2 - x1, y2 - y1)
    const ratio = dist / Math.max(1, pinchStartDist.value)
    const next = Math.min(4, Math.max(0.5, pinchStartScale.value * ratio))
    pdfScale.value = next
    return
  }
  if (!pdfDragTouching.value) return
  const t = e.touches[0] || e.changedTouches[0]
  if (!t) return
  const dy = t.clientY - pdfLastY.value
  const dx = t.clientX - pdfLastX.value
  if (el) el.scrollTop -= dy
  if (el) el.scrollLeft -= dx
  pdfLastY.value = t.clientY
  pdfLastX.value = t.clientX
}
const schedulePdfRerender = () => {
  if (pdfRerenderTimer) clearTimeout(pdfRerenderTimer)
  pdfRerenderTimer = setTimeout(async () => {
    if (!pdfDoc || !pdfContainer.value) return
    const el = pdfContainer.value
    const count = pdfDoc.numPages || 0
    const scale = Math.min(4, Math.max(0.5, pdfBaseScale * pdfScale.value))
    el.innerHTML = ''
    pdfLoading.value = true
    try {
      for (let i = 1; i <= count; i++) {
        const page = await pdfDoc.getPage(i)
        const v = page.getViewport({ scale })
        const canvas = document.createElement('canvas')
        canvas.width = Math.floor(v.width)
        canvas.height = Math.floor(v.height)
        const ctx = canvas.getContext('2d')!
        await page.render({ canvasContext: ctx, viewport: v }).promise
        el.appendChild(canvas)
      }
    } catch {}
    pdfLoading.value = false
    pdfScale.value = 1
    pdfTransformOrigin.value = '50% 0'
  }, 200)
}
const onPdfTouchEnd = () => {
  if (pinchActive.value) {
    pinchActive.value = false
    schedulePdfRerender()
  }
  pdfDragTouching.value = false
}
const onPdfMouseDown = (e: MouseEvent) => {
  pdfDragMouseDown.value = true
  pdfLastY.value = e.clientY
  pdfLastX.value = e.clientX
}
const onPdfMouseMove = (e: MouseEvent) => {
  if (!pdfDragMouseDown.value) return
  const dy = e.clientY - pdfLastY.value
  const el = pdfContainer.value
  if (el) el.scrollTop -= dy
  pdfLastY.value = e.clientY
  const dx = e.clientX - pdfLastX.value
  if (el) el.scrollLeft -= dx
  pdfLastX.value = e.clientX
}
const onPdfMouseUp = () => {
  pdfDragMouseDown.value = false
}

const handleUploadItem = (group: any) => {
  currentUploadGroup.value = group
  if (fileInput.value) {
    fileInput.value.value = ''
    fileInput.value.click()
  }
}

const checkFileFormat = (fileName: string, extensions: string[]) => {
  if (extensions.length === 0) return true

  const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase()
  return extensions.includes(ext)
}

const onFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (input.files && input.files.length > 0 && currentUploadGroup.value) {
    const file = input.files[0] as File

    // Validate file size by flow
    const sizeLimitMB = Number(currentUploadGroup.value?.sizeLimitMB ?? (isDCFlow.value ? 100 : 20))
    const maxSize = sizeLimitMB * 1024 * 1024
    if (file.size > maxSize) {
      showToast(`File size must be ≤ ${sizeLimitMB}MB`)
      input.value = ''
      return
    }

    // Validate file format
    const formatConfig = getFormat(currentUploadGroup.value.docType)
    if (!checkFileFormat(file.name, formatConfig.extensions)) {
      showToast(`File format must be ${formatConfig.label}`)
      input.value = ''
      return
    }

    loading.value = true
    try {
      const extraParams: any = {}
      // N6节点的上传文件需要额外参数
      if (currentTaskName.value.startsWith('N6. ')) {
        extraParams.storeDepth = storeDepth.value
        extraParams.storeWidth = storeWidth.value
        extraParams.storeHeight = storeHeight.value
      }

      const fileName = file.name || ''
      const lastDotIndex = fileName.lastIndexOf('.')
      const nameWithoutExt = lastDotIndex > -1 ? fileName.substring(0, lastDotIndex) : fileName

      await uploadAttachment(
        batchId.value,
        clcDoorCode.value,
        taskId.value,
        file,
        currentUploadGroup.value.docType,
        nameWithoutExt,
        processDefinitionKey.value,
        extraParams,
      )
      showToast('Upload successful')
      fetchData()
    } catch (error) {
      console.error('Upload failed:', error)
      showToast('Upload failed')
    } finally {
      loading.value = false
      currentUploadGroup.value = null
      // Clear input value to allow re-uploading the same file if needed (though we return early on error, successful upload might need clearing too)
      input.value = ''
    }
  }
}

const handleDownloadItem = async (item: any) => {
  try {
    if (item.url) {
      // 请求后端生成带 Content-Disposition: attachment 的预签名 URL
      const res = await getPresignedUrl(item.url, true)
      const downloadUrl = res.data

      if (downloadUrl) {
        // 直接创建一个隐藏的 a 标签点击下载
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = downloadUrl
        link.download = item.oriName || 'download'
        document.body.appendChild(link)
        link.click()

        // 延时清理
        setTimeout(() => {
          document.body.removeChild(link)
        }, 100)
      } else {
        showToast('Unable to get download link')
      }
    } else {
      showToast('File identifier (url) does not exist')
    }
  } catch (error) {
    console.error('Download failed:', error)
    showToast('Download failed')
  }
}

onMounted(async () => {
  const cached = sessionStorage.getItem('user_name') || ''
  if (cached) {
    userName.value = cached
  } else {
    try {
      const result = await getCurrentUser()
      userName.value = String((result as any)?.data?.user?.userName || '')
      if (userName.value) sessionStorage.setItem('user_name', userName.value)
    } catch {}
  }
  
  if (isDoorSummaryAllorOpened.value) {
    await userStore.loadUser()
  }

  fetchData()
})

watch(showViewer, async (val) => {
  if (!val) await clearPdf()
})
</script>

<style scoped lang="scss">
.attachment-page {
  background-color: #fff;
  min-height: 100vh;
}

.wrapper {
  position: relative;
}
.wm-overlay-in-viewer {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
  background-repeat: repeat;
  background-size: 260px 260px;
}
.viewer-close-btn {
  z-index: 2;
}

@media (orientation: landscape) {
  .attachment-page {
    zoom: 0.5;
  }
}

.store-header {
  font-size: 14px;
  color: #003d7c;
}

.file-title {
  font-size: 14px;
  color: #323233;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-type-tag {
  font-size: 14px;
  color: #a62736;
}

.file-ori-name {
  font-size: 14px;
  color: #167ce5;
  word-break: break-all;
}

.action-btn {
  height: 24px;
  padding: 0 8px;
  font-size: 12px;
}

.download-btn {
  height: 24px;
  padding: 0 8px;
  font-size: 12px;
  background-color: #fff;
  border: 1px solid #333;
  color: #333;
}
.download-btn :deep(.van-button__content) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-icon {
  width: 14px;
  height: 14px;
}
.dark-btn {
  height: 24px;
  padding: 0 10px;
  font-size: 12px;
  border-radius: 12px;
  border: 1px solid rgb(29, 29, 29);
  background-color: rgb(29, 29, 29);
  color: #fff;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.doc-viewer-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.doc-viewer-html {
  width: 100%;
  height: 100%;
  overflow: auto;
  background: #fff;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
  overflow: auto;
  background: #fff;
  touch-action: none;
}

.pdf-loading {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  pointer-events: none;
  z-index: 2;
}
</style>
